/* ==========================================================================
   SilentRise Theme JS — Premium Sleep Tech Wearable Store
   Vanilla ES6+ | No frameworks | Production-ready
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     0. GLOBAL CONFIG & UTILITIES
     ======================================================================== */

  const SR = window.SilentRise || {};
  const routes = SR.routes || {
    cart_url: '/cart',
    cart_add_url: '/cart/add.js',
    cart_change_url: '/cart/change.js',
    cart_update_url: '/cart/update.js',
    search_url: '/search'
  };

  const SELECTORS = {
    cartDrawer: '#cart-drawer',
    cartDrawerBackdrop: '#cart-drawer-backdrop',
    cartDrawerClose: '[data-cart-drawer-close]',
    cartDrawerOpen: '[data-cart-drawer-open]',
    cartCount: '[data-cart-count]',
    cartItems: '[data-cart-items]',
    cartSubtotal: '[data-cart-subtotal]',
    cartEmpty: '[data-cart-empty]',
    cartContent: '[data-cart-content]',
    cartShippingBar: '[data-shipping-bar]',
    cartShippingMessage: '[data-shipping-message]',
    cartShippingProgress: '[data-shipping-progress]',
    cartDiscountInput: '[data-discount-input]',
    cartDiscountBtn: '[data-discount-submit]',
    cartNotes: '[data-cart-notes]',
    cartUpsells: '[data-cart-upsells]',
    addToCart: '[data-add-to-cart]',
    stickyBar: '#sticky-add-to-cart',
    variantBtn: '.variant-btn',
    qtyPlus: '[data-qty-plus]',
    qtyMinus: '[data-qty-minus]',
    qtyInput: '[data-qty-input]',
    mobileMenuToggle: '[data-mobile-menu-toggle]',
    mobileMenu: '#mobile-menu',
    mobileMenuClose: '[data-mobile-menu-close]',
    mobileSubmenuToggle: '[data-submenu-toggle]',
    headerDropdown: '[data-dropdown]',
    headerDropdownTrigger: '[data-dropdown-trigger]',
    countdown: '[data-countdown]',
    faqQuestion: '.faq__question',
    faqAnswer: '.faq__answer',
    faqItem: '.faq__item',
    newsletterPopup: '#newsletter-popup',
    newsletterClose: '[data-popup-close]',
    newsletterForm: '.newsletter-popup__form',
    announcementDismiss: '[data-announcement-dismiss]',
    announcementBar: '[data-announcement-bar]',
    animate: '[data-animate]',
    animateStagger: '[data-animate-stagger]',
    countUp: '[data-count-up]',
    quiz: '.quiz',
    quizStep: '.quiz__step',
    quizAnswer: '.quiz__answer',
    quizProgress: '.quiz__progress-fill',
    quizResult: '.quiz__result',
    smoothScroll: 'a[href^="#"]',
    lazyBg: '[data-bg]',
    tabs: '.product__tabs',
    tabHeader: '[data-tab]',
    tabPanel: '[data-tab-panel]',
    galleryThumb: '[data-gallery-thumb]',
    galleryMain: '[data-gallery-main]',
    galleryMainImg: '[data-gallery-main-img]',
    productPrice: '[data-product-price]',
    productComparePrice: '[data-product-compare-price]',
    productSaleBadge: '[data-sale-badge]',
    productAvailability: '[data-availability]',
    productLowStock: '[data-low-stock]',
    productTitle: '[data-product-title]',
    stickyBarPrice: '[data-sticky-price]',
    stickyBarTitle: '[data-sticky-title]',
    stickyBarVariantSelect: '[data-sticky-variant-select]',
    stickyBarAddToCart: '[data-sticky-add-to-cart]'
  };

  /* ---------- Utility: formatMoney ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = format || SR.moneyFormat || '£{{amount}}';

    function addCommas(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function addDotsAndComma(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    if (!match) return fmt;

    const placeholder = match[1];
    let value;
    switch (placeholder) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100));
        break;
      case 'amount_with_comma_separator':
        value = addDotsAndComma((cents / 100).toFixed(2)).replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addDotsAndComma(Math.round(cents / 100));
        break;
      case 'amount_with_space_separator':
        value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        break;
      default:
        value = (cents / 100).toFixed(2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  /* ---------- Utility: fetch helpers ---------- */
  function fetchJSON(url, options) {
    const defaults = {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
    return fetch(url, Object.assign(defaults, options)).then(function (res) {
      if (!res.ok) return res.json().then(function (err) { throw err; });
      return res.json();
    });
  }

  /* ---------- Utility: debounce ---------- */
  function debounce(fn, delay) {
    let timer;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(context, args); }, delay);
    };
  }

  /* ---------- Utility: trapFocus ---------- */
  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input:not([type="hidden"]):not([disabled]), select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return null;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handler);
    first.focus();
    return function release() {
      container.removeEventListener('keydown', handler);
    };
  }

  /* ---------- Utility: body scroll lock ---------- */
  let scrollLockCount = 0;
  const scrollLockStyle = { top: '', position: '', width: '', overflow: '' };

  function lockBodyScroll() {
    if (scrollLockCount === 0) {
      const scrollY = window.scrollY;
      scrollLockStyle.top = document.body.style.top;
      scrollLockStyle.position = document.body.style.position;
      scrollLockStyle.width = document.body.style.width;
      scrollLockStyle.overflow = document.body.style.overflow;
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.dataset.scrollY = scrollY;
    }
    scrollLockCount++;
  }

  function unlockBodyScroll() {
    scrollLockCount--;
    if (scrollLockCount <= 0) {
      scrollLockCount = 0;
      const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      document.body.style.top = scrollLockStyle.top;
      document.body.style.position = scrollLockStyle.position;
      document.body.style.width = scrollLockStyle.width;
      document.body.style.overflow = scrollLockStyle.overflow;
      window.scrollTo(0, scrollY);
    }
  }

  /* ---------- Utility: prefersReducedMotion ---------- */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Utility: dispatch custom event ---------- */
  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }


  /* ========================================================================
     1. CART DRAWER
     ======================================================================== */

  const CartDrawer = (function () {
    let cart = null;
    let releaseFocus = null;
    let previousActiveElement = null;

    function getDrawer() { return document.querySelector(SELECTORS.cartDrawer); }
    function getBackdrop() { return document.querySelector(SELECTORS.cartDrawerBackdrop); }

    function open(skipFetch) {
      const drawer = getDrawer();
      if (!drawer) return;
      previousActiveElement = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockBodyScroll();
      releaseFocus = trapFocus(drawer);
      if (!skipFetch) fetchCart().then(render);
    }

    function close() {
      const drawer = getDrawer();
      const backdrop = getBackdrop();
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.classList.remove('is-visible');
      }
      unlockBodyScroll();
      if (releaseFocus) {
        releaseFocus();
        releaseFocus = null;
      }
      if (previousActiveElement) {
        previousActiveElement.focus();
        previousActiveElement = null;
      }
    }

    function isOpen() {
      const drawer = getDrawer();
      return drawer && drawer.classList.contains('is-open');
    }

    function fetchCart() {
      return fetchJSON('/cart.js').then(function (data) {
        cart = data;
        return data;
      });
    }

    function updateCount(count) {
      document.querySelectorAll(SELECTORS.cartCount).forEach(function (el) {
        el.textContent = count;
        el.setAttribute('aria-label', count + ' items in cart');
        if (count > 0) {
          el.classList.add('has-items');
        } else {
          el.classList.remove('has-items');
        }
      });
    }

    /* --- Section-based rendering (single source of truth from Liquid) --- */
    function refreshSection() {
      return fetch('/?sections=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var drawer = getDrawer();
        if (!drawer) return;

        /* Replace items */
        var newItems = tmp.querySelector('[data-cart-items]');
        var oldItems = drawer.querySelector('[data-cart-items]');
        if (newItems && oldItems) oldItems.innerHTML = newItems.innerHTML;

        /* Replace upsells */
        var newUpsells = tmp.querySelector('[data-cart-upsells]');
        var oldUpsells = drawer.querySelector('[data-cart-upsells]');
        if (oldUpsells && newUpsells) {
          oldUpsells.innerHTML = newUpsells.innerHTML;
        } else if (oldUpsells && !newUpsells) {
          oldUpsells.remove();
        }
      })
      .catch(function () {});
    }

    function render(data) {
      if (!data) return;
      cart = data;
      var drawer = getDrawer();
      if (!drawer) return;

      updateCount(data.item_count);

      var bodyEl = drawer.querySelector('[data-cart-body]');
      var footerEl = drawer.querySelector('[data-cart-footer]');
      var emptyState = drawer.querySelector('[data-cart-empty]');
      var subtotalEl = drawer.querySelector(SELECTORS.cartSubtotal);

      if (data.item_count === 0) {
        if (bodyEl) bodyEl.hidden = true;
        if (footerEl) footerEl.hidden = true;
        if (emptyState) emptyState.hidden = false;
      } else {
        if (bodyEl) bodyEl.hidden = false;
        if (footerEl) footerEl.hidden = false;
        if (emptyState) emptyState.hidden = true;
      }

      if (subtotalEl) subtotalEl.textContent = formatMoney(data.total_price);

      /* Update shipping bar */
      var shippingBar = drawer.querySelector('[data-shipping-bar]');
      if (shippingBar) {
        var threshold = parseInt(shippingBar.dataset.threshold, 10) || 0;
        if (threshold > 0) {
          var remaining = Math.max(0, threshold - data.total_price);
          var pct = Math.min(100, (data.total_price / threshold) * 100);
          var fill = shippingBar.querySelector('[data-shipping-fill]');
          var text = shippingBar.querySelector('[data-shipping-text]');
          if (fill) fill.style.width = pct + '%';
          if (text) {
            text.innerHTML = remaining > 0
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;color:#B8860B"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> You\'re <strong>' + formatMoney(remaining) + '</strong> away from <strong>15% off your next order</strong>'
              : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" style="vertical-align:-3px"><path d="M5 13l4 4L19 7"/></svg> <strong style="color:#22c55e">15% off unlocked!</strong> Code: NEXT15';
          }
        }
      }

      /* Fetch fresh Liquid-rendered section HTML for items & upsells */
      refreshSection();

      dispatchCartUpdated(data);
    }

    function changeQuantity(key, quantity) {
      return fetchJSON(routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (data) {
        render(data);
        return data;
      });
    }

    function removeItem(key) {
      return changeQuantity(key, 0);
    }

    function addToCart(items) {
      return fetchJSON(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: items })
      }).then(function () {
        return fetchCart().then(function (data) {
          render(data);
          return data;
        });
      });
    }

    function updateNote(note) {
      return fetchJSON(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ note: note })
      }).then(function (data) {
        cart = data;
        dispatchCartUpdated(data);
      });
    }

    function applyDiscount(code) {
      return fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin'
      }).then(function (res) {
        if (res.ok) {
          return fetchCart().then(function (data) {
            render(data);
            Toast.show('Discount applied!', 'success');
            return data;
          });
        }
        throw new Error('Invalid discount code');
      });
    }

    function bindEvents() {
      /* Open triggers */
      document.addEventListener('click', function (e) {
        const openBtn = e.target.closest(SELECTORS.cartDrawerOpen);
        if (openBtn) {
          e.preventDefault();
          open();
        }
      });

      /* Close triggers */
      document.addEventListener('click', function (e) {
        if (e.target.closest(SELECTORS.cartDrawerClose)) {
          e.preventDefault();
          close();
        }
        const backdrop = getBackdrop();
        if (backdrop && e.target === backdrop) {
          close();
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) {
          close();
        }
      });

      /* Cart item quantity and removal (event delegation inside drawer) */
      const drawer = getDrawer();
      if (drawer) {
        drawer.addEventListener('click', function (e) {
          const removeBtn = e.target.closest('[data-remove-item]');
          if (removeBtn) {
            e.preventDefault();
            const key = removeBtn.getAttribute('data-remove-item') || removeBtn.getAttribute('data-key');
            if (key) removeItem(key);
            return;
          }

          const plusBtn = e.target.closest('[data-qty-plus]');
          if (plusBtn) {
            const key = plusBtn.getAttribute('data-cart-line-key');
            const display = drawer.querySelector('[data-qty-display][data-cart-line-key="' + key + '"]');
            if (display) {
              const current = parseInt(display.textContent, 10) || 1;
              const newVal = Math.min(current + 1, 99);
              display.textContent = newVal;
              changeQuantity(key, newVal);
            }
            return;
          }

          const minusBtn = e.target.closest('[data-qty-minus]');
          if (minusBtn) {
            const key = minusBtn.getAttribute('data-cart-line-key');
            const display = drawer.querySelector('[data-qty-display][data-cart-line-key="' + key + '"]');
            if (display) {
              const current = parseInt(display.textContent, 10) || 1;
              const newVal = Math.max(current - 1, 0);
              if (newVal === 0) {
                removeItem(key);
              } else {
                display.textContent = newVal;
                changeQuantity(key, newVal);
              }
            }
            return;
          }
        });

        /* Cart notes */
        drawer.addEventListener('input', debounce(function (e) {
          if (e.target.matches(SELECTORS.cartNotes)) {
            updateNote(e.target.value);
          }
        }, 500));

        /* Discount code */
        drawer.addEventListener('click', function (e) {
          const discountBtn = e.target.closest(SELECTORS.cartDiscountBtn);
          if (!discountBtn) return;
          e.preventDefault();
          const input = drawer.querySelector(SELECTORS.cartDiscountInput);
          if (!input || !input.value.trim()) return;
          const code = input.value.trim();
          discountBtn.disabled = true;
          discountBtn.textContent = 'Applying...';
          applyDiscount(code).catch(function () {
            Toast.show('Invalid discount code', 'error');
          }).finally(function () {
            discountBtn.disabled = false;
            discountBtn.textContent = 'Apply';
          });
        });
      }
    }

    function init() {
      bindEvents();
      /* Pre-fetch cart to set initial count */
      fetchCart().then(function (data) {
        updateCount(data.item_count);
      });
    }

    return {
      init: init,
      open: open,
      close: close,
      addToCart: addToCart,
      fetchCart: fetchCart,
      render: render,
      updateCount: updateCount
    };
  })();


  /* ========================================================================
     2. ADD TO CART
     ======================================================================== */

  const AddToCart = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        const btn = e.target.closest(SELECTORS.addToCart);
        if (!btn) return;
        e.preventDefault();

        const form = btn.closest('form');
        let variantId;
        let quantity = 1;

        if (btn.hasAttribute('data-variant-id')) {
          variantId = btn.getAttribute('data-variant-id');
        } else if (form) {
          const idInput = form.querySelector('[name="id"]');
          if (idInput) variantId = idInput.value;
        }

        if (!variantId) {
          Toast.show('Please select a variant', 'error');
          return;
        }

        const qtyInput = form ? form.querySelector(SELECTORS.qtyInput) : null;
        if (qtyInput) {
          quantity = parseInt(qtyInput.value, 10) || 1;
        }
        if (btn.hasAttribute('data-quantity')) {
          quantity = parseInt(btn.getAttribute('data-quantity'), 10) || 1;
        }

        /* Loading state */
        const originalText = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = '<span class="spinner"></span> Adding...';

        CartDrawer.addToCart([{ id: parseInt(variantId, 10), quantity: quantity }])
          .then(function () {
            btn.innerHTML = 'Added!';
            if (SR.cartType === 'drawer') {
              CartDrawer.open(true);
            }
            Toast.show('Item added to cart', 'success');
            setTimeout(function () {
              btn.innerHTML = originalText;
              btn.disabled = false;
            }, 1500);
          })
          .catch(function (err) {
            const message = (err && err.description) || (err && err.message) || 'Not enough stock';
            Toast.show(message, 'error');
            btn.innerHTML = originalText;
            btn.disabled = false;
          });
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     3. STICKY ADD TO CART BAR
     ======================================================================== */

  const StickyBar = (function () {
    let observer = null;

    function init() {
      const mainATC = document.querySelector('.product-form ' + SELECTORS.addToCart);
      const stickyBar = document.querySelector(SELECTORS.stickyBar);
      if (!mainATC || !stickyBar) return;

      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            stickyBar.classList.add('is-visible');
            stickyBar.setAttribute('aria-hidden', 'false');
          }
        });
      }, { threshold: 0 });

      observer.observe(mainATC);

      /* Sync variant selector in sticky bar */
      const stickySelect = stickyBar.querySelector(SELECTORS.stickyBarVariantSelect);
      if (stickySelect) {
        stickySelect.addEventListener('change', function () {
          const variantId = stickySelect.value;
          /* Trigger variant change on the main page */
          document.querySelectorAll(SELECTORS.variantBtn).forEach(function (btn) {
            if (btn.getAttribute('data-variant-id') === variantId) {
              btn.click();
            }
          });
        });
      }

      /* Sticky bar ATC */
      const stickyATC = stickyBar.querySelector(SELECTORS.stickyBarAddToCart);
      if (stickyATC) {
        stickyATC.addEventListener('click', function (e) {
          e.preventDefault();
          const variantId = stickyATC.getAttribute('data-variant-id') ||
            (stickySelect ? stickySelect.value : null);
          if (!variantId) return;

          const originalText = stickyATC.innerHTML;
          stickyATC.disabled = true;
          stickyATC.innerHTML = '<span class="spinner"></span> Adding...';

          CartDrawer.addToCart([{ id: parseInt(variantId, 10), quantity: 1 }])
            .then(function () {
              stickyATC.innerHTML = 'Added!';
              if (SR.cartType === 'drawer') CartDrawer.open(true);
              Toast.show('Item added to cart', 'success');
              setTimeout(function () {
                stickyATC.innerHTML = originalText;
                stickyATC.disabled = false;
              }, 1500);
            })
            .catch(function (err) {
              const message = (err && err.description) || (err && err.message) || 'Not enough stock';
              Toast.show(message, 'error');
              stickyATC.innerHTML = originalText;
              stickyATC.disabled = false;
            });
        });
      }
    }

    return { init: init };
  })();


  /* ========================================================================
     4. VARIANT SELECTOR
     ======================================================================== */

  const VariantSelector = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        const btn = e.target.closest(SELECTORS.variantBtn);
        if (!btn) return;
        e.preventDefault();

        const container = btn.closest('[data-variant-container]') || btn.parentElement;
        const variantId = btn.getAttribute('data-variant-id');
        const price = btn.getAttribute('data-variant-price');
        const comparePrice = btn.getAttribute('data-variant-compare-price');
        const available = btn.getAttribute('data-variant-available') !== 'false';
        const imageId = btn.getAttribute('data-variant-image-id');
        const imageIndex = btn.getAttribute('data-variant-image-index');
        const inventory = parseInt(btn.getAttribute('data-variant-inventory'), 10);

        /* Toggle active state */
        container.querySelectorAll(SELECTORS.variantBtn).forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        /* Update price */
        document.querySelectorAll(SELECTORS.productPrice).forEach(function (el) {
          if (price) el.innerHTML = formatMoney(parseInt(price, 10));
        });

        /* Compare at price */
        document.querySelectorAll(SELECTORS.productComparePrice).forEach(function (el) {
          if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
            el.innerHTML = formatMoney(parseInt(comparePrice, 10));
            el.style.display = '';
          } else {
            el.innerHTML = '';
            el.style.display = 'none';
          }
        });

        /* Sale badge */
        document.querySelectorAll(SELECTORS.productSaleBadge).forEach(function (el) {
          if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });

        /* Availability */
        document.querySelectorAll(SELECTORS.productAvailability).forEach(function (el) {
          el.textContent = available ? 'In Stock' : 'Sold Out';
          el.classList.toggle('is-sold-out', !available);
        });

        /* Low stock warning */
        const lowThreshold = SR.lowStockThreshold || 10;
        document.querySelectorAll(SELECTORS.productLowStock).forEach(function (el) {
          if (available && !isNaN(inventory) && inventory > 0 && inventory <= lowThreshold) {
            el.textContent = 'Only ' + inventory + ' left in stock!';
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });

        /* Update ATC button state */
        document.querySelectorAll(SELECTORS.addToCart).forEach(function (atcBtn) {
          if (!atcBtn.closest(SELECTORS.stickyBar) && !atcBtn.closest(SELECTORS.cartDrawer)) {
            if (available) {
              atcBtn.disabled = false;
              atcBtn.textContent = 'Add to Cart';
            } else {
              atcBtn.disabled = true;
              atcBtn.textContent = 'Sold Out';
            }
            atcBtn.setAttribute('data-variant-id', variantId);
          }
        });

        /* Update hidden form input */
        const form = document.querySelector('.product-form');
        if (form) {
          const idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = variantId;
        }

        /* Update URL */
        if (variantId && window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
        }

        /* Update product image gallery */
        if (imageIndex !== null && imageIndex !== undefined) {
          const idx = parseInt(imageIndex, 10);
          const thumbs = document.querySelectorAll(SELECTORS.galleryThumb);
          if (thumbs[idx]) {
            thumbs[idx].click();
          }
        } else if (imageId) {
          const thumb = document.querySelector(SELECTORS.galleryThumb + '[data-image-id="' + imageId + '"]');
          if (thumb) thumb.click();
        }

        /* Sync sticky bar */
        const stickyBar = document.querySelector(SELECTORS.stickyBar);
        if (stickyBar) {
          const stickySelect = stickyBar.querySelector(SELECTORS.stickyBarVariantSelect);
          if (stickySelect) stickySelect.value = variantId;

          const stickyPrice = stickyBar.querySelector(SELECTORS.stickyBarPrice);
          if (stickyPrice && price) stickyPrice.innerHTML = formatMoney(parseInt(price, 10));

          const stickyATC = stickyBar.querySelector(SELECTORS.stickyBarAddToCart);
          if (stickyATC) {
            stickyATC.setAttribute('data-variant-id', variantId);
            if (available) {
              stickyATC.disabled = false;
              stickyATC.textContent = 'Add to Cart';
            } else {
              stickyATC.disabled = true;
              stickyATC.textContent = 'Sold Out';
            }
          }
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     5. QUANTITY SELECTOR (Product page)
     ======================================================================== */

  const QuantitySelector = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        /* Plus button (non-cart-drawer context is handled here; cart drawer has its own) */
        const plus = e.target.closest(SELECTORS.qtyPlus);
        if (plus && !plus.hasAttribute('data-cart-line-key')) {
          const wrapper = plus.closest('.quantity-selector') || plus.parentElement;
          const input = wrapper.querySelector(SELECTORS.qtyInput);
          if (!input) return;
          const max = parseInt(input.getAttribute('data-max') || input.getAttribute('max'), 10) || 99;
          const current = parseInt(input.value, 10) || 1;
          input.value = Math.min(current + 1, max);
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        /* Minus button */
        const minus = e.target.closest(SELECTORS.qtyMinus);
        if (minus && !minus.hasAttribute('data-cart-line-key')) {
          const wrapper = minus.closest('.quantity-selector') || minus.parentElement;
          const input = wrapper.querySelector(SELECTORS.qtyInput);
          if (!input) return;
          const current = parseInt(input.value, 10) || 1;
          input.value = Math.max(current - 1, 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      });

      /* Enforce min/max on direct input */
      document.addEventListener('change', function (e) {
        const input = e.target.closest(SELECTORS.qtyInput);
        if (!input || input.hasAttribute('data-cart-line-key')) return;
        const min = parseInt(input.getAttribute('min'), 10) || 1;
        const max = parseInt(input.getAttribute('data-max') || input.getAttribute('max'), 10) || 99;
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) val = min;
        if (val > max) val = max;
        input.value = val;
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     6. PRODUCT IMAGE GALLERY
     ======================================================================== */

  const ProductGallery = (function () {
    let touchStartX = 0;
    let touchStartY = 0;
    let currentIndex = 0;

    function init() {
      const thumbs = document.querySelectorAll(SELECTORS.galleryThumb);
      const mainImgWrap = document.querySelector(SELECTORS.galleryMain);
      const mainImg = document.querySelector(SELECTORS.galleryMainImg);
      if (!mainImg || thumbs.length === 0) return;

      /* Thumbnail clicks */
      thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          currentIndex = index;
          const src = thumb.getAttribute('data-full-src') || thumb.getAttribute('href') || thumb.querySelector('img').src;
          const alt = thumb.getAttribute('data-alt') || (thumb.querySelector('img') ? thumb.querySelector('img').alt : '');
          mainImg.src = src;
          mainImg.alt = alt;

          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });

      /* Activate first thumb by default */
      if (thumbs.length > 0 && !document.querySelector(SELECTORS.galleryThumb + '.is-active')) {
        thumbs[0].classList.add('is-active');
      }

      /* Zoom on hover (desktop) */
      if (mainImgWrap) {
        mainImgWrap.addEventListener('mouseenter', function () {
          mainImg.style.transformOrigin = '50% 50%';
          mainImg.style.transition = 'transform 0.3s ease';
          mainImg.style.transform = 'scale(1.8)';
        });

        mainImgWrap.addEventListener('mousemove', function (e) {
          const rect = mainImgWrap.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          mainImg.style.transformOrigin = x + '% ' + y + '%';
        });

        mainImgWrap.addEventListener('mouseleave', function () {
          mainImg.style.transform = 'scale(1)';
          mainImg.style.transformOrigin = '50% 50%';
        });

        /* Touch swipe for mobile */
        mainImgWrap.addEventListener('touchstart', function (e) {
          touchStartX = e.changedTouches[0].clientX;
          touchStartY = e.changedTouches[0].clientY;
        }, { passive: true });

        mainImgWrap.addEventListener('touchend', function (e) {
          const deltaX = e.changedTouches[0].clientX - touchStartX;
          const deltaY = e.changedTouches[0].clientY - touchStartY;

          /* Only handle horizontal swipes */
          if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > Math.abs(deltaX)) return;

          if (deltaX < 0 && currentIndex < thumbs.length - 1) {
            /* Swipe left -> next */
            thumbs[currentIndex + 1].click();
          } else if (deltaX > 0 && currentIndex > 0) {
            /* Swipe right -> prev */
            thumbs[currentIndex - 1].click();
          }
        }, { passive: true });
      }

      /* Lazy load gallery images (all except first) */
      thumbs.forEach(function (thumb, i) {
        if (i === 0) return;
        const img = thumb.querySelector('img');
        if (img && !img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     7. MOBILE MENU
     ======================================================================== */

  const MobileMenu = (function () {
    let releaseFocus = null;
    let previousActiveElement = null;

    function getMenu() { return document.querySelector(SELECTORS.mobileMenu); }

    function open() {
      const menu = getMenu();
      if (!menu) return;
      previousActiveElement = document.activeElement;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      lockBodyScroll();
      releaseFocus = trapFocus(menu);
    }

    function close() {
      const menu = getMenu();
      if (!menu) return;
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      unlockBodyScroll();
      if (releaseFocus) {
        releaseFocus();
        releaseFocus = null;
      }
      if (previousActiveElement) {
        previousActiveElement.focus();
        previousActiveElement = null;
      }
    }

    function isOpen() {
      const menu = getMenu();
      return menu && menu.classList.contains('is-open');
    }

    function init() {
      /* Toggle open */
      document.addEventListener('click', function (e) {
        const toggle = e.target.closest(SELECTORS.mobileMenuToggle);
        if (toggle) {
          e.preventDefault();
          if (isOpen()) {
            close();
          } else {
            open();
          }
          return;
        }

        const closeBtn = e.target.closest(SELECTORS.mobileMenuClose);
        if (closeBtn) {
          e.preventDefault();
          close();
          return;
        }

        /* Sub-menu toggles */
        const submenuToggle = e.target.closest(SELECTORS.mobileSubmenuToggle);
        if (submenuToggle) {
          e.preventDefault();
          const parentItem = submenuToggle.closest('.mobile-nav__item') || submenuToggle.parentElement;
          const submenu = parentItem.querySelector('.mobile-nav__submenu');
          if (!submenu) return;

          const isExpanded = submenuToggle.getAttribute('aria-expanded') === 'true';
          submenuToggle.setAttribute('aria-expanded', String(!isExpanded));
          submenu.classList.toggle('is-open');

          if (!isExpanded) {
            submenu.style.maxHeight = submenu.scrollHeight + 'px';
          } else {
            submenu.style.maxHeight = '0';
          }
        }
      });

      /* Close on escape */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) {
          close();
        }
      });
    }

    return { init: init, open: open, close: close };
  })();


  /* ========================================================================
     8. HEADER DROPDOWN NAVIGATION
     ======================================================================== */

  const HeaderDropdowns = (function () {
    const hideTimers = new Map();

    function showDropdown(trigger) {
      const dropdown = trigger.querySelector(SELECTORS.headerDropdown) ||
        document.getElementById(trigger.getAttribute('aria-controls'));
      if (!dropdown) return;

      const timerId = hideTimers.get(trigger);
      if (timerId) {
        clearTimeout(timerId);
        hideTimers.delete(trigger);
      }

      trigger.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('is-open');
      dropdown.setAttribute('aria-hidden', 'false');
    }

    function hideDropdown(trigger) {
      const dropdown = trigger.querySelector(SELECTORS.headerDropdown) ||
        document.getElementById(trigger.getAttribute('aria-controls'));
      if (!dropdown) return;

      const timerId = setTimeout(function () {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
        dropdown.setAttribute('aria-hidden', 'true');
        hideTimers.delete(trigger);
      }, 200);

      hideTimers.set(trigger, timerId);
    }

    function closeAll() {
      document.querySelectorAll(SELECTORS.headerDropdownTrigger).forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        const dropdown = trigger.querySelector(SELECTORS.headerDropdown) ||
          document.getElementById(trigger.getAttribute('aria-controls'));
        if (dropdown) {
          dropdown.classList.remove('is-open');
          dropdown.setAttribute('aria-hidden', 'true');
        }
      });
      hideTimers.forEach(function (timerId) { clearTimeout(timerId); });
      hideTimers.clear();
    }

    function init() {
      document.querySelectorAll(SELECTORS.headerDropdownTrigger).forEach(function (trigger) {
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');

        trigger.addEventListener('mouseenter', function () { showDropdown(trigger); });
        trigger.addEventListener('mouseleave', function () { hideDropdown(trigger); });

        /* Keep dropdown open when hovering over it */
        const dropdown = trigger.querySelector(SELECTORS.headerDropdown) ||
          document.getElementById(trigger.getAttribute('aria-controls'));
        if (dropdown) {
          dropdown.addEventListener('mouseenter', function () {
            const timerId = hideTimers.get(trigger);
            if (timerId) {
              clearTimeout(timerId);
              hideTimers.delete(trigger);
            }
          });
          dropdown.addEventListener('mouseleave', function () {
            hideDropdown(trigger);
          });
        }
      });

      /* Close on click outside */
      document.addEventListener('click', function (e) {
        if (!e.target.closest(SELECTORS.headerDropdownTrigger)) {
          closeAll();
        }
      });

      /* Close on escape */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeAll();
        }
      });
    }

    return { init: init, closeAll: closeAll };
  })();


  /* ========================================================================
     9. COUNTDOWN TIMER
     ======================================================================== */

  const CountdownTimer = (function () {
    let intervalId = null;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function update() {
      const endStr = SR.countdownEnd;
      if (!endStr) return;

      const end = new Date(endStr.replace(' ', 'T')).getTime();
      const now = Date.now();
      const diff = end - now;
      const targets = document.querySelectorAll(SELECTORS.countdown);
      if (targets.length === 0) return;

      if (diff <= 0) {
        targets.forEach(function (el) {
          if (el.hasAttribute('data-countdown-expired-text')) {
            el.textContent = el.getAttribute('data-countdown-expired-text');
          } else {
            el.textContent = 'Sale Ended';
          }
          el.classList.add('is-expired');
        });
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      targets.forEach(function (el) {
        const daysEl = el.querySelector('[data-countdown-days]');
        const hoursEl = el.querySelector('[data-countdown-hours]');
        const minutesEl = el.querySelector('[data-countdown-minutes]');
        const secondsEl = el.querySelector('[data-countdown-seconds]');

        if (daysEl) daysEl.textContent = pad(days);
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minutesEl) minutesEl.textContent = pad(minutes);
        if (secondsEl) secondsEl.textContent = pad(seconds);

        /* Fallback: if no sub-elements, render as string */
        if (!daysEl && !hoursEl && !minutesEl && !secondsEl) {
          el.textContent = days + 'd ' + pad(hours) + 'h ' + pad(minutes) + 'm ' + pad(seconds) + 's';
        }
      });
    }

    function init() {
      if (!SR.countdownEnd) return;
      if (document.querySelectorAll(SELECTORS.countdown).length === 0) return;
      update();
      intervalId = setInterval(update, 1000);
    }

    return { init: init };
  })();


  /* ========================================================================
     10. FAQ ACCORDION
     ======================================================================== */

  const FaqAccordion = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        const question = e.target.closest(SELECTORS.faqQuestion);
        if (!question) return;
        e.preventDefault();

        const item = question.closest(SELECTORS.faqItem) || question.parentElement;
        const answer = item.querySelector(SELECTORS.faqAnswer);
        if (!answer) return;

        const isOpen = question.getAttribute('aria-expanded') === 'true';

        /* Close all other items */
        const container = item.closest('.faq') || item.parentElement;
        container.querySelectorAll(SELECTORS.faqItem).forEach(function (otherItem) {
          if (otherItem === item) return;
          const otherQ = otherItem.querySelector(SELECTORS.faqQuestion);
          const otherA = otherItem.querySelector(SELECTORS.faqAnswer);
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
          if (otherA) {
            otherA.style.maxHeight = '0';
            otherA.setAttribute('aria-hidden', 'true');
          }
          otherItem.classList.remove('is-open');
        });

        /* Toggle current */
        if (isOpen) {
          question.setAttribute('aria-expanded', 'false');
          answer.style.maxHeight = '0';
          answer.setAttribute('aria-hidden', 'true');
          item.classList.remove('is-open');
        } else {
          question.setAttribute('aria-expanded', 'true');
          answer.style.maxHeight = answer.scrollHeight + 'px';
          answer.setAttribute('aria-hidden', 'false');
          item.classList.add('is-open');
        }
      });

      /* Set initial ARIA attributes */
      document.querySelectorAll(SELECTORS.faqItem).forEach(function (item, i) {
        const question = item.querySelector(SELECTORS.faqQuestion);
        const answer = item.querySelector(SELECTORS.faqAnswer);
        if (!question || !answer) return;

        const id = answer.id || 'faq-answer-' + i;
        answer.id = id;
        question.setAttribute('aria-expanded', 'false');
        question.setAttribute('aria-controls', id);
        question.setAttribute('role', 'button');
        question.setAttribute('tabindex', '0');
        answer.setAttribute('role', 'region');
        answer.setAttribute('aria-hidden', 'true');
        answer.style.maxHeight = '0';
        answer.style.overflow = 'hidden';
        answer.style.transition = 'max-height 0.35s ease';
      });

      /* Allow keyboard activation */
      document.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.closest(SELECTORS.faqQuestion)) {
          e.preventDefault();
          e.target.closest(SELECTORS.faqQuestion).click();
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     11. NEWSLETTER POPUP
     ======================================================================== */

  const NewsletterPopup = (function () {
    const STORAGE_KEY = 'sr_newsletter_dismissed';
    const DISMISS_DAYS = 7;
    let shown = false;

    function isDismissed() {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return false;
      const dismissedAt = parseInt(stored, 10);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      return daysSince < DISMISS_DAYS;
    }

    function dismiss() {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      close();
    }

    function getPopup() { return document.querySelector(SELECTORS.newsletterPopup); }

    function open() {
      const popup = getPopup();
      if (!popup || shown) return;
      shown = true;
      popup.classList.add('is-visible');
      popup.setAttribute('aria-hidden', 'false');
    }

    function close() {
      const popup = getPopup();
      if (!popup) return;
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
    }

    function init() {
      const popup = getPopup();
      if (!popup || isDismissed()) return;

      /* Show after 8 seconds */
      setTimeout(function () {
        if (!isDismissed() && !shown) open();
      }, 8000);

      /* Exit intent (desktop) */
      document.addEventListener('mouseleave', function (e) {
        if (e.clientY <= 0 && !isDismissed() && !shown) {
          open();
        }
      });

      /* Close button */
      document.addEventListener('click', function (e) {
        if (e.target.closest(SELECTORS.newsletterClose)) {
          e.preventDefault();
          dismiss();
          return;
        }
        /* Click outside */
        if (popup.classList.contains('is-visible') && e.target === popup) {
          dismiss();
        }
      });

      /* Escape key */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && popup.classList.contains('is-visible')) {
          dismiss();
        }
      });

      /* Form submission */
      const form = popup.querySelector(SELECTORS.newsletterForm);
      if (form) {
        const submitBtn = form.querySelector('.newsletter-popup__submit');
        const handleNewsletterSubmit = function (e) {
          e.preventDefault();
          e.stopPropagation();
          localStorage.setItem(STORAGE_KEY, String(Date.now()));
          const emailInput = form.querySelector('input[type="email"]');
          const email = emailInput ? emailInput.value.trim() : '';
          if (!email) return;

          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Subscribing...';
          }

          const iframeName = 'sr-newsletter-frame-' + Date.now();
          const hiddenFrame = document.createElement('iframe');
          hiddenFrame.name = iframeName;
          hiddenFrame.style.display = 'none';
          document.body.appendChild(hiddenFrame);

          const realForm = document.createElement('form');
          realForm.method = 'post';
          realForm.action = '/contact#contact_form';
          realForm.target = iframeName;
          realForm.style.display = 'none';

          const submittedFields = new FormData(form);
          submittedFields.set('contact[email]', email);
          submittedFields.forEach(function (value, key) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = value;
            realForm.appendChild(hiddenField);
          });

          document.body.appendChild(realForm);
          realForm.submit();

          setTimeout(function () {
            const formContent = popup.querySelector('.newsletter-popup__form-content') || form;
            formContent.innerHTML = '<div class="newsletter-popup__success"><h3>Confirmed!</h3><p>Your code is ready for you at checkout.</p></div>';
            setTimeout(dismiss, 4000);
          }, 1200);

          setTimeout(function () {
            hiddenFrame.remove();
            realForm.remove();
          }, 8000);
        };
        form.addEventListener('submit', handleNewsletterSubmit);
        if (submitBtn) { submitBtn.addEventListener('click', handleNewsletterSubmit); }
      }
    }

    return { init: init };
  })();


  /* ========================================================================
     12. ANNOUNCEMENT BAR
     ======================================================================== */

  const AnnouncementBar = (function () {
    const STORAGE_KEY = 'sr_announcement_dismissed';

    function init() {
      const bar = document.querySelector(SELECTORS.announcementBar);
      if (!bar) return;

      /* Check if already dismissed this session */
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        bar.style.display = 'none';
        return;
      }

      document.addEventListener('click', function (e) {
        const dismissBtn = e.target.closest(SELECTORS.announcementDismiss);
        if (!dismissBtn) return;
        e.preventDefault();
        bar.style.transition = 'max-height 0.4s ease, opacity 0.3s ease, margin 0.4s ease, padding 0.4s ease';
        bar.style.maxHeight = bar.scrollHeight + 'px';
        /* Force reflow */
        void bar.offsetHeight;
        bar.style.maxHeight = '0';
        bar.style.opacity = '0';
        bar.style.overflow = 'hidden';
        bar.style.marginTop = '0';
        bar.style.marginBottom = '0';
        bar.style.paddingTop = '0';
        bar.style.paddingBottom = '0';
        sessionStorage.setItem(STORAGE_KEY, 'true');
        bar.addEventListener('transitionend', function handler() {
          bar.style.display = 'none';
          bar.removeEventListener('transitionend', handler);
        });
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     13. SCROLL ANIMATIONS (IntersectionObserver)
     ======================================================================== */

  const ScrollAnimations = (function () {
    function init() {
      if (!SR.enableAnimations || prefersReducedMotion()) {
        /* Show all elements immediately without animation */
        document.querySelectorAll(SELECTORS.animate).forEach(function (el) {
          el.classList.add('is-visible');
        });
        return;
      }

      /* Lower threshold + rootMargin triggers elements earlier so they're
         already animating in by the time the user scrolls to them */
      const isMobile = window.innerWidth < 750;
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          const el = entry.target;

          if (el.hasAttribute('data-animate-stagger')) {
            /* Stagger children — faster on mobile */
            const children = el.querySelectorAll('[data-animate]');
            const staggerMs = isMobile ? 40 : 80;
            children.forEach(function (child, i) {
              setTimeout(function () {
                child.classList.add('is-visible');
              }, i * staggerMs);
            });
            el.classList.add('is-visible');
          } else {
            /* On mobile, use CSS transition-delay only (no JS setTimeout)
               to keep things snappy and avoid timer overhead */
            if (isMobile) {
              el.classList.add('is-visible');
            } else {
              var delay = parseInt(el.getAttribute('data-animate-delay'), 10);
              if (delay > 0) {
                setTimeout(function () {
                  el.classList.add('is-visible');
                }, delay);
              } else {
                el.classList.add('is-visible');
              }
            }
          }

          observer.unobserve(el);
        });
      }, { threshold: 0.02, rootMargin: '0px 0px 60px 0px' });

      document.querySelectorAll(SELECTORS.animate).forEach(function (el) {
        observer.observe(el);
      });

      document.querySelectorAll(SELECTORS.animateStagger).forEach(function (el) {
        if (!el.hasAttribute('data-animate')) {
          observer.observe(el);
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     13b. PARALLAX ENGINE — Scroll-driven depth layers
     ======================================================================== */

  const ParallaxEngine = (function () {
    var elements = [];
    var ticking = false;
    var scrollY = 0;

    function onScroll() {
      scrollY = window.pageYOffset;
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    function update() {
      ticking = false;
      var wh = window.innerHeight;
      for (var i = 0; i < elements.length; i++) {
        var item = elements[i];
        if (!item.visible) continue;
        var rect = item.el.getBoundingClientRect();
        var center = rect.top + rect.height * 0.5;
        var offset = (center - wh * 0.5) * item.speed;
        item.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      }
    }

    function init() {
      if (prefersReducedMotion()) return;
      if (window.innerWidth < 750) return; /* Skip on mobile for perf */

      var nodes = document.querySelectorAll('[data-parallax]');
      if (nodes.length === 0) return;

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          for (var i = 0; i < elements.length; i++) {
            if (elements[i].el === entry.target) {
              elements[i].visible = entry.isIntersecting;
              break;
            }
          }
        });
      }, { threshold: 0, rootMargin: '100px' });

      nodes.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.1;
        elements.push({ el: el, speed: speed, visible: false });
        observer.observe(el);
      });

      window.addEventListener('scroll', onScroll, { passive: true });
    }

    return { init: init };
  })();


  /* ========================================================================
     13c. TILT EFFECT — Mouse-tracking 3D hover for cards
     ======================================================================== */

  const TiltEffect = (function () {
    function init() {
      if (prefersReducedMotion()) return;
      /* Desktop only */
      if (!window.matchMedia('(hover: hover)').matches) return;

      var els = document.querySelectorAll('[data-tilt]');
      if (els.length === 0) return;

      var maxAngle = 8;

      els.forEach(function (el) {
        var custom = parseFloat(el.getAttribute('data-tilt'));
        var angle = (custom && custom > 0 && custom <= 20) ? custom : maxAngle;

        el.addEventListener('mousemove', function (e) {
          var rect = el.getBoundingClientRect();
          var x = (e.clientX - rect.left) / rect.width;
          var y = (e.clientY - rect.top) / rect.height;
          var rotateY = (x - 0.5) * angle * 2;
          var rotateX = (0.5 - y) * angle * 2;

          el.style.transform = 'perspective(800px) rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg) translateZ(8px)';

          /* Glare position */
          if (el.hasAttribute('data-tilt-glare')) {
            el.style.setProperty('--glare-x', (x * 100).toFixed(1) + '%');
            el.style.setProperty('--glare-y', (y * 100).toFixed(1) + '%');
          }
        });

        el.addEventListener('mouseleave', function () {
          el.style.transform = '';
          if (el.hasAttribute('data-tilt-glare')) {
            el.style.setProperty('--glare-x', '50%');
            el.style.setProperty('--glare-y', '50%');
          }
        });
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     13d. DEPTH SCROLL — Section-level 3D perspective on scroll
     ======================================================================== */

  const DepthScroll = (function () {
    var sections = [];
    var ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    function update() {
      ticking = false;
      var wh = window.innerHeight;
      for (var i = 0; i < sections.length; i++) {
        var item = sections[i];
        var rect = item.el.getBoundingClientRect();
        /* How far section center is from viewport center, normalized -1 to 1 */
        var center = rect.top + rect.height * 0.5;
        var pct = Math.max(-1, Math.min(1, (center - wh * 0.5) / (wh * 0.8)));
        var rx = pct * 1.5; /* Max 1.5deg rotation */
        var tz = Math.abs(pct) * -15; /* Max -15px depth when at edges */
        item.el.style.setProperty('--depth-rx', rx.toFixed(2) + 'deg');
        item.el.style.setProperty('--depth-tz', tz.toFixed(1) + 'px');
      }
    }

    function init() {
      if (prefersReducedMotion()) return;
      if (window.innerWidth < 750) return;

      var els = document.querySelectorAll('[data-depth-section]');
      if (els.length === 0) return;

      els.forEach(function (el) {
        sections.push({ el: el });
      });

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll(); /* Initial position */
    }

    return { init: init };
  })();


  /* ========================================================================
     14. ANIMATED NUMBER COUNTERS
     ======================================================================== */

  const NumberCounters = (function () {
    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    function animateCounter(el) {
      const target = parseFloat(el.getAttribute('data-count-up')) || 0;
      const suffix = el.getAttribute('data-count-suffix') || '';
      const prefix = el.getAttribute('data-count-prefix') || '';
      const decimals = (String(target).split('.')[1] || '').length;
      const duration = 2000;
      const startTime = performance.now();

      function step(now) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        const current = easedProgress * target;
        el.textContent = prefix + current.toFixed(decimals) + suffix;
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.textContent = prefix + target + suffix;
        }
      }

      requestAnimationFrame(step);
    }

    function init() {
      const elements = document.querySelectorAll(SELECTORS.countUp);
      if (elements.length === 0) return;

      if (prefersReducedMotion()) {
        elements.forEach(function (el) {
          const target = el.getAttribute('data-count-up') || '0';
          const suffix = el.getAttribute('data-count-suffix') || '';
          const prefix = el.getAttribute('data-count-prefix') || '';
          el.textContent = prefix + target + suffix;
        });
        return;
      }

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.1 });

      elements.forEach(function (el) {
        el.textContent = (el.getAttribute('data-count-prefix') || '') + '0' + (el.getAttribute('data-count-suffix') || '');
        observer.observe(el);
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     15. QUIZ FUNCTIONALITY
     ======================================================================== */

  const Quiz = (function () {
    function init() {
      const quizContainers = document.querySelectorAll(SELECTORS.quiz);
      quizContainers.forEach(function (quiz) {
        setupQuiz(quiz);
      });
    }

    function setupQuiz(container) {
      const steps = container.querySelectorAll(SELECTORS.quizStep);
      const progressBar = container.querySelector(SELECTORS.quizProgress);
      const resultEl = container.querySelector(SELECTORS.quizResult);
      if (steps.length === 0) return;

      let currentStep = 0;
      const answers = {};
      const totalSteps = steps.length;

      function showStep(index) {
        steps.forEach(function (step, i) {
          step.classList.toggle('is-active', i === index);
          step.style.display = i === index ? '' : 'none';
          step.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
        });
        if (progressBar) {
          progressBar.style.width = ((index + 1) / totalSteps * 100) + '%';
        }
      }

      function calculateResult() {
        /* Score-based recommendation: tally up answers */
        let score = 0;
        Object.keys(answers).forEach(function (key) {
          const val = parseInt(answers[key], 10);
          if (!isNaN(val)) score += val;
        });

        /* Always recommend Pro — it's the hero product. Standard framed as a downgrade. */
        if (resultEl) {
          var proReason = (score >= (totalSteps * 2))
            ? 'You need the full vibration power of the Pro — its 5 escalating intensity levels are built for heavy sleepers like you.'
            : 'Based on your answers, the SilentRise Pro is the right fit. 5 vibration levels, 10-day battery, and the confidence of knowing you\'ll never sleep through an alarm again.';
          resultEl.innerHTML = '<div class="quiz__recommendation">' +
            '<h3>SilentRise Pro</h3>' +
            '<p>' + proReason + '</p>' +
            '<a href="/products/silentrise" class="btn btn--primary">Shop SilentRise Pro</a>' +
          '</div>';
          resultEl.style.display = '';
          resultEl.setAttribute('aria-hidden', 'false');
        }
      }

      /* Initialize */
      showStep(0);
      if (resultEl) {
        resultEl.style.display = 'none';
        resultEl.setAttribute('aria-hidden', 'true');
      }

      /* Listen for answer clicks */
      container.addEventListener('click', function (e) {
        const answerBtn = e.target.closest(SELECTORS.quizAnswer);
        if (!answerBtn) return;
        e.preventDefault();

        const stepEl = answerBtn.closest(SELECTORS.quizStep);
        const stepIndex = Array.prototype.indexOf.call(steps, stepEl);
        const value = answerBtn.getAttribute('data-answer-value') || '0';
        const key = stepEl.getAttribute('data-step-key') || ('step-' + stepIndex);

        /* Mark selected */
        stepEl.querySelectorAll(SELECTORS.quizAnswer).forEach(function (btn) {
          btn.classList.remove('is-selected');
        });
        answerBtn.classList.add('is-selected');

        answers[key] = value;

        /* Advance */
        if (stepIndex < totalSteps - 1) {
          currentStep = stepIndex + 1;
          setTimeout(function () { showStep(currentStep); }, 300);
        } else {
          /* Final step: show result */
          setTimeout(function () {
            steps.forEach(function (step) {
              step.style.display = 'none';
              step.setAttribute('aria-hidden', 'true');
            });
            if (progressBar) progressBar.style.width = '100%';
            calculateResult();
          }, 300);
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     16. SMOOTH SCROLL
     ======================================================================== */

  const SmoothScroll = (function () {
    function init() {
      document.addEventListener('click', function (e) {
        const link = e.target.closest(SELECTORS.smoothScroll);
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href || href === '#' || href.length < 2) return;

        /* Only handle same-page anchors */
        const targetId = href.substring(1);
        const target = document.getElementById(targetId);
        if (!target) return;

        e.preventDefault();

        const header = document.querySelector('.site-header') || document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;
        const targetTop = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

        if (prefersReducedMotion()) {
          window.scrollTo(0, targetTop);
        } else {
          window.scrollTo({ top: targetTop, behavior: 'smooth' });
        }

        /* Update URL hash without jump */
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', href);
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     17. LAZY LOADING (Background images)
     ======================================================================== */

  const LazyLoad = (function () {
    function init() {
      const lazyBgEls = document.querySelectorAll(SELECTORS.lazyBg);
      if (lazyBgEls.length === 0) return;

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const bgUrl = el.getAttribute('data-bg');
          if (bgUrl) {
            el.style.backgroundImage = 'url(' + bgUrl + ')';
            el.removeAttribute('data-bg');
            el.classList.add('is-loaded');
          }
          observer.unobserve(el);
        });
      }, { rootMargin: '200px 0px' });

      lazyBgEls.forEach(function (el) { observer.observe(el); });
    }

    return { init: init };
  })();


  /* ========================================================================
     18. MONEY FORMATTING (exposed globally)
     ======================================================================== */

  window.SilentRise = window.SilentRise || {};
  window.SilentRise.formatMoney = formatMoney;


  /* ========================================================================
     19. TABS
     ======================================================================== */

  const Tabs = (function () {
    function init() {
      const tabContainers = document.querySelectorAll(SELECTORS.tabs);
      tabContainers.forEach(function (container) {
        setupTabs(container);
      });
    }

    function setupTabs(container) {
      const headers = container.querySelectorAll(SELECTORS.tabHeader);
      const panels = container.querySelectorAll(SELECTORS.tabPanel);
      if (headers.length === 0 || panels.length === 0) return;

      /* Set ARIA roles */
      const tablistEl = headers[0].parentElement;
      if (tablistEl) tablistEl.setAttribute('role', 'tablist');

      headers.forEach(function (header, i) {
        const panelId = header.getAttribute('data-tab');
        const panel = container.querySelector('[data-tab-panel="' + panelId + '"]');

        header.setAttribute('role', 'tab');
        header.setAttribute('tabindex', i === 0 ? '0' : '-1');
        header.id = header.id || 'tab-' + panelId;
        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', header.id);
          panel.id = panel.id || 'tabpanel-' + panelId;
          header.setAttribute('aria-controls', panel.id);
        }

        if (i === 0) {
          header.setAttribute('aria-selected', 'true');
          header.classList.add('is-active');
          if (panel) panel.style.display = '';
        } else {
          header.setAttribute('aria-selected', 'false');
          if (panel) panel.style.display = 'none';
        }
      });

      /* Click handler */
      headers.forEach(function (header) {
        header.addEventListener('click', function (e) {
          e.preventDefault();
          activateTab(container, header, headers, panels);
        });

        /* Keyboard: arrow keys */
        header.addEventListener('keydown', function (e) {
          let index = Array.prototype.indexOf.call(headers, header);
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            index = (index + 1) % headers.length;
            headers[index].focus();
            activateTab(container, headers[index], headers, panels);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            index = (index - 1 + headers.length) % headers.length;
            headers[index].focus();
            activateTab(container, headers[index], headers, panels);
          }
        });
      });
    }

    function activateTab(container, activeHeader, headers, panels) {
      headers.forEach(function (h) {
        h.setAttribute('aria-selected', 'false');
        h.setAttribute('tabindex', '-1');
        h.classList.remove('is-active');
      });

      activeHeader.setAttribute('aria-selected', 'true');
      activeHeader.setAttribute('tabindex', '0');
      activeHeader.classList.add('is-active');

      const activePanelId = activeHeader.getAttribute('data-tab');
      panels.forEach(function (panel) {
        if (panel.getAttribute('data-tab-panel') === activePanelId) {
          panel.style.display = '';
        } else {
          panel.style.display = 'none';
        }
      });
    }

    return { init: init };
  })();


  /* ========================================================================
     20. TOAST NOTIFICATIONS
     ======================================================================== */

  const Toast = (function () {
    let container = null;

    function getContainer() {
      if (container && document.body.contains(container)) return container;
      container = document.createElement('div');
      container.className = 'toast-container';
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'false');
      document.body.appendChild(container);
      return container;
    }

    function show(message, type) {
      type = type || 'info';
      const wrap = getContainer();
      const toast = document.createElement('div');
      toast.className = 'toast toast--' + type;
      toast.setAttribute('role', 'status');
      toast.textContent = message;

      /* Close button */
      const closeBtn = document.createElement('button');
      closeBtn.className = 'toast__close';
      closeBtn.setAttribute('aria-label', 'Dismiss notification');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', function () { removeToast(toast); });
      toast.appendChild(closeBtn);

      wrap.appendChild(toast);

      /* Trigger animation */
      void toast.offsetHeight;
      toast.classList.add('is-visible');

      /* Auto dismiss */
      const timer = setTimeout(function () { removeToast(toast); }, 4000);
      toast.dataset.timer = timer;
    }

    function removeToast(toast) {
      if (toast.dataset.timer) clearTimeout(parseInt(toast.dataset.timer, 10));
      toast.classList.remove('is-visible');
      toast.classList.add('is-leaving');
      toast.addEventListener('transitionend', function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      });
      /* Fallback removal */
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 500);
    }

    return { show: show };
  })();

  /* Expose toast globally */
  window.SilentRise.showToast = Toast.show;


  /* ========================================================================
     UTILITY: escapeHTML
     ======================================================================== */

  function escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }


  /* ========================================================================
     INJECT CRITICAL STYLES (Toast, Cart Drawer, Sticky Bar, Animations)
     Inline styles injected via JS to ensure modules work independently
     ======================================================================== */

  function injectStyles() {
    const style = document.createElement('style');
    style.setAttribute('data-silentrise-js-styles', '');
    style.textContent = [
      /* Toast */
      '.toast-container{position:fixed;top:20px;right:20px;z-index:10000;display:flex;flex-direction:column;gap:8px;pointer-events:none;}',
      '.toast{pointer-events:auto;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;border-radius:8px;color:#fff;font-size:14px;line-height:1.4;box-shadow:0 4px 20px rgba(0,0,0,.15);transform:translateX(120%);transition:transform .3s ease,opacity .3s ease;max-width:380px;min-width:280px;}',
      '.toast.is-visible{transform:translateX(0);}',
      '.toast.is-leaving{transform:translateX(120%);opacity:0;}',
      '.toast--success{background:#0d9f6e;}',
      '.toast--error{background:#e02424;}',
      '.toast--info{background:#1c64f2;}',
      '.toast__close{background:none;border:none;color:inherit;font-size:18px;cursor:pointer;padding:0 0 0 8px;line-height:1;opacity:.8;}',
      '.toast__close:hover{opacity:1;}',

      /* Cart Drawer */
      '#cart-drawer{position:fixed;top:0;right:0;bottom:0;width:420px;max-width:90vw;background:#fff;z-index:9999;transform:translateX(100%);transition:transform .35s cubic-bezier(.4,0,.2,1);overflow-y:auto;box-shadow:-4px 0 30px rgba(0,0,0,.1);}',
      '#cart-drawer.is-open{transform:translateX(0);}',
      '#cart-drawer-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:9998;opacity:0;pointer-events:none;transition:opacity .3s ease;}',
      '#cart-drawer-backdrop.is-visible{opacity:1;pointer-events:auto;}',

      /* Sticky ATC Bar */
      '#sticky-add-to-cart{position:fixed;bottom:0;left:0;right:0;background:#fff;z-index:999;transform:translateY(100%);transition:transform .35s cubic-bezier(.4,0,.2,1);box-shadow:0 -2px 20px rgba(0,0,0,.1);padding:12px 20px;}',
      '#sticky-add-to-cart.is-visible{transform:translateY(0);}',

      /* Scroll Animations */
      '[data-animate]{opacity:0;transition:opacity .6s ease,transform .6s ease;}',
      '[data-animate="fade-in"]{opacity:0;}',
      '[data-animate="slide-up"]{transform:translateY(30px);}',
      '[data-animate="slide-left"]{transform:translateX(30px);}',
      '[data-animate="slide-right"]{transform:translateX(-30px);}',
      '[data-animate="scale-in"]{transform:scale(0.85);}',
      '[data-animate].is-visible{opacity:1;transform:translate(0,0) scale(1);}',

      /* Mobile Menu */
      '#mobile-menu{position:fixed;top:0;left:0;bottom:0;width:320px;max-width:85vw;background:#fff;z-index:9999;transform:translateX(-100%);transition:transform .35s cubic-bezier(.4,0,.2,1);overflow-y:auto;box-shadow:4px 0 30px rgba(0,0,0,.1);}',
      '#mobile-menu.is-open{transform:translateX(0);}',
      '.mobile-nav__submenu{max-height:0;overflow:hidden;transition:max-height .35s ease;}',

      /* Header Dropdowns */
      '[data-dropdown]{opacity:0;transform:translateY(-8px);pointer-events:none;transition:opacity .25s ease,transform .25s ease;position:absolute;}',
      '[data-dropdown].is-open{opacity:1;transform:translateY(0);pointer-events:auto;}',

      /* Cart Shipping Bar */
      '.cart-shipping__bar{height:6px;background:#e5e7eb;border-radius:3px;overflow:hidden;margin-top:8px;}',
      '.cart-shipping__progress{height:100%;background:#0d9f6e;border-radius:3px;transition:width .4s ease;}',
      '.cart-shipping__message--success{color:#0d9f6e;font-weight:600;}',

      /* FAQ */
      '.faq__answer{overflow:hidden;transition:max-height .35s ease;}',

      /* Newsletter Popup */
      '#newsletter-popup{position:fixed;inset:0;z-index:10001;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.5);opacity:0;pointer-events:none;transition:opacity .3s ease;}',
      '#newsletter-popup.is-visible{opacity:1;pointer-events:auto;}',

      /* Spinner */
      '.spinner{display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:sr-spin .6s linear infinite;vertical-align:middle;margin-right:4px;}',
      '@keyframes sr-spin{to{transform:rotate(360deg)}}',

      /* Cart count badge */
      '[data-cart-count].has-items{display:inline-flex;}',

      /* Gallery zoom */
      '[data-gallery-main]{overflow:hidden;cursor:zoom-in;}',
      '[data-gallery-main] img{transition:transform .3s ease;}',
      '[data-gallery-thumb].is-active{border-color:currentColor;outline:2px solid currentColor;outline-offset:2px;}',

      /* Reduced motion */
      '@media(prefers-reduced-motion:reduce){[data-animate]{opacity:1!important;transform:none!important;transition:none!important;}#cart-drawer,#mobile-menu,#sticky-add-to-cart,.toast,[data-dropdown],#newsletter-popup{transition:none!important;}}'
    ].join('\n');

    document.head.appendChild(style);
  }


  /* ========================================================================
     INIT — Boot all modules on DOMContentLoaded
     ======================================================================== */

  function boot() {
    injectStyles();
    CartDrawer.init();
    AddToCart.init();
    StickyBar.init();
    VariantSelector.init();
    QuantitySelector.init();
    ProductGallery.init();
    MobileMenu.init();
    HeaderDropdowns.init();
    CountdownTimer.init();
    FaqAccordion.init();
    NewsletterPopup.init();
    AnnouncementBar.init();
    ScrollAnimations.init();
    ParallaxEngine.init();
    TiltEffect.init();
    DepthScroll.init();
    NumberCounters.init();
    Quiz.init();
    SmoothScroll.init();
    LazyLoad.init();
    Tabs.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
